import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { getDb } from './firebase';

const PAGES_COLLECTION = 'pages';

// HTML'i düz metne çeviren yardımcı fonksiyon
const convertHtmlToText = (html) => {
  // Geçici bir div oluştur
  const temp = document.createElement('div');
  // HTML içeriği div'e ekle
  temp.innerHTML = html;
  // Script ve style taglerini kaldır
  const scripts = temp.getElementsByTagName('script');
  const styles = temp.getElementsByTagName('style');
  while (scripts[0]) scripts[0].parentNode.removeChild(scripts[0]);
  while (styles[0]) styles[0].parentNode.removeChild(styles[0]);
  // Düz metni al
  return temp.textContent || temp.innerText;
};

// Metinden akıllı description oluşturan fonksiyon
const generateSmartDescription = (text, maxLength = 160) => {
  // Metni temizle ve boşlukları düzenle
  const cleanText = text.replace(/\s+/g, ' ').trim();
  
  // İlk cümleyi bul
  let firstSentence = cleanText.split(/[.!?]/).filter(s => s.trim())[0] || '';
  
  // Eğer ilk cümle çok uzunsa, kelime bazlı kes
  if (firstSentence.length > maxLength) {
    const words = firstSentence.split(' ');
    firstSentence = '';
    for (const word of words) {
      if ((firstSentence + word).length <= maxLength - 3) {
        firstSentence += (firstSentence ? ' ' : '') + word;
      } else {
        break;
      }
    }
    firstSentence += '...';
  }

  return firstSentence;
};

// Sayfa içeriğinden meta description oluşturan fonksiyon
const generateMetaFromContent = (content) => {
  // HTML'i düz metne çevir
  const plainText = convertHtmlToText(content);
  // Akıllı description oluştur
  return generateSmartDescription(plainText);
};

// JSX içeriğini HTML'e dönüştüren yardımcı fonksiyon
const convertJsxToHtml = (jsxContent) => {
  try {
    if (typeof jsxContent === 'string') return jsxContent;
    if (!jsxContent) return '';

    // JSX nesnesini HTML'e dönüştür
    const processNode = (node) => {
      if (!node) return '';
      if (typeof node === 'string') return node;
      if (Array.isArray(node)) return node.map(processNode).join('');

      // Özel bileşenleri işle
      if (typeof node.type === 'function') {
        try {
          const Component = node.type;
          const rendered = Component(node.props);
          return processNode(rendered);
        } catch (error) {
          console.error('Özel bileşen işlenirken hata:', error);
          return '';
        }
      }

      // React Fragment veya benzeri özel tipleri kontrol et
      if (typeof node.type === 'symbol' || !node.props) {
        return processNode(node.props?.children);
      }

      // HTML elementlerini oluştur
      const { children, className, dangerouslySetInnerHTML, ...props } = node.props;

      // dangerouslySetInnerHTML varsa direkt içeriği kullan
      if (dangerouslySetInnerHTML) {
        return dangerouslySetInnerHTML.__html;
      }

      // Diğer props'ları HTML attribute'larına dönüştür
      const attributes = Object.entries(props)
        .filter(([key, value]) => value !== undefined && value !== null)
        .map(([key, value]) => {
          // Event handler'ları ve özel React prop'larını atla
          if (key.startsWith('on') || key === 'ref' || key === 'key') return '';
          // camelCase'i kebab-case'e çevir
          const attr = key.replace(/([A-Z])/g, '-$1').toLowerCase();
          return `${attr}="${value}"`;
        })
        .filter(Boolean)
        .join(' ');

      const classAttr = className ? ` class="${className}"` : '';
      const tag = node.type;

      if (!children && !dangerouslySetInnerHTML) {
        return `<${tag}${classAttr}${attributes ? ' ' + attributes : ''}/>`;
      }

      return `<${tag}${classAttr}${attributes ? ' ' + attributes : ''}>${processNode(children)}</${tag}>`;
    };

    // Tüm içeriği işle
    let result = processNode(jsxContent);

    // HTML'i temizle ve düzenle
    result = result
      .replace(/\s+/g, ' ')
      .replace(/> </g, '>\n<')
      .trim();

    return result;
  } catch (error) {
    console.error('JSX to HTML conversion error:', error);
    return '';
  }
};

// URL'yi slug formatına dönüştüren fonksiyon
const generateSlug = (text) => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

// Sayfa verilerini hazırlayan fonksiyon
const preparePageData = (pageData) => {
  const preparedData = { ...pageData };

  // İçeriği HTML formatında sakla
  if (pageData.content) {
    // JSX içeriğini HTML'e dönüştür
    const htmlContent = convertJsxToHtml(pageData.content);
    preparedData.content = htmlContent;
    preparedData.plainContent = convertHtmlToText(htmlContent);
    
    // Sayfanın tam içeriğini de sakla
    preparedData.fullContent = pageData.content;
  }

  // Eğer metaDescription yoksa veya boşsa, içerikten oluştur
  if (!preparedData.metaDescription && preparedData.content) {
    preparedData.metaDescription = generateMetaFromContent(preparedData.content);
  }

  // Eğer metaTitle yoksa, title'ı kullan
  if (!preparedData.metaTitle && preparedData.title) {
    preparedData.metaTitle = `${preparedData.title} | Talent Finder`;
  }

  return preparedData;
};

// Sayfa oluştur
export const createPage = async (pageData) => {
  try {
    const db = await getDb();
    const pagesRef = collection(db, PAGES_COLLECTION);
    const docRef = await addDoc(pagesRef, {
      ...pageData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating page:', error);
    throw error;
  }
};

// Sayfa güncelle
export const updatePage = async (pageId, pageData) => {
  try {
    const db = await getDb();
    const pageRef = doc(db, PAGES_COLLECTION, pageId);
    await updateDoc(pageRef, {
      ...pageData,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error updating page:', error);
    throw error;
  }
};

// Sayfa sil
export const deletePage = async (pageId) => {
  try {
    const db = await getDb();
    const pageRef = doc(db, PAGES_COLLECTION, pageId);
    await deleteDoc(pageRef);
    return true;
  } catch (error) {
    console.error('Error deleting page:', error);
    throw error;
  }
};

// Tek sayfa getir
export const getPage = async (pageId) => {
  try {
    const db = await getDb();
    const pageRef = doc(db, PAGES_COLLECTION, pageId);
    const pageSnap = await getDoc(pageRef);
    
    if (pageSnap.exists()) {
      return {
        id: pageSnap.id,
        ...pageSnap.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting page:', error);
    throw error;
  }
};

// Tüm sayfaları getir
export const getAllPages = async () => {
  try {
    const db = await getDb();
    const pagesRef = collection(db, PAGES_COLLECTION);
    const q = query(pagesRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting all pages:', error);
    throw error;
  }
};

// Yayınlanmış sayfaları getir
export const getPublishedPages = async () => {
  try {
    const db = await getDb();
    const pagesRef = collection(db, PAGES_COLLECTION);
    const q = query(
      pagesRef, 
      where('status', '==', 'published'),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting published pages:', error);
    throw error;
  }
};

// Sayfa durumunu güncelle
export const updatePageStatus = async (pageId, status) => {
  try {
    const db = await getDb();
    const pageRef = doc(db, PAGES_COLLECTION, pageId);
    await updateDoc(pageRef, {
      status,
      updatedAt: serverTimestamp()
    });
    return { id: pageId, status };
  } catch (error) {
    console.error('Sayfa durumu güncellenirken hata:', error);
    throw error;
  }
};

// Sayfayı kalıcı olarak sil
export const permanentlyDeletePage = async (pageId) => {
  try {
    const db = await getDb();
    const pageRef = doc(db, PAGES_COLLECTION, pageId);
    await deleteDoc(pageRef);
    return { id: pageId };
  } catch (error) {
    console.error('Sayfa kalıcı olarak silinirken hata:', error);
    throw error;
  }
};

// Silinen sayfayı geri yükle
export const restorePage = async (pageId) => {
  try {
    const db = await getDb();
    const pageRef = doc(db, PAGES_COLLECTION, pageId);
    await updateDoc(pageRef, {
      status: 'draft',
      deletedAt: null,
      updatedAt: serverTimestamp()
    });
    return { id: pageId };
  } catch (error) {
    console.error('Sayfa geri yüklenirken hata:', error);
    throw error;
  }
};

// Özel bileşenlerin içeriğini çıkaran fonksiyon
const extractComponentContent = (rendered) => {
  try {
    if (!rendered || !rendered.props) return '';
    
    let content = '';
    const children = rendered.props.children;
    
    // Özel bileşenleri işleme fonksiyonu
    const processComponent = (child) => {
      if (!child || !child.type) return '';

      // Bileşen adını al
      const componentName = child.type.name || 'anonymous';

      // Özel bileşenleri işle
      if (typeof child.type === 'function') {
        try {
          const Component = child.type;
          const rendered = Component(child.props);

          // PageHeader bileşeni için özel işleme
          if (componentName === 'PageHeader') {
            return `<div class="page-header">
              <h1>${child.props.title || ''}</h1>
              <p>${child.props.description || ''}</p>
            </div>`;
          }

          // Hero bileşeni için özel işleme
          if (componentName === 'Hero') {
            return `<div class="hero-component">
              ${convertJsxToHtml(rendered)}
            </div>`;
          }

          // Services bileşeni için özel işleme
          if (componentName === 'Services') {
            return `<div class="services-component">
              ${convertJsxToHtml(rendered)}
            </div>`;
          }

          // HRPartnership bileşeni için özel işleme
          if (componentName === 'HRPartnership') {
            return `<div class="hr-partnership-component">
              ${convertJsxToHtml(rendered)}
            </div>`;
          }

          // SectionCTA bileşeni için özel işleme
          if (componentName === 'SectionCTA') {
            return `<div class="section-cta-component">
              ${convertJsxToHtml(rendered)}
            </div>`;
          }

          // WhyUs bileşeni için özel işleme
          if (componentName === 'WhyUs') {
            return `<div class="why-us-component">
              ${convertJsxToHtml(rendered)}
            </div>`;
          }

          // CVForm bileşeni için özel işleme
          if (componentName === 'CVForm') {
            return `<div class="cv-form-component">
              ${convertJsxToHtml(rendered)}
            </div>`;
          }

          // ServicesSection bileşeni için özel işleme
          if (componentName === 'ServicesSection') {
            return `<div class="services-section-component">
              ${convertJsxToHtml(rendered)}
            </div>`;
          }

          // BlogList bileşeni için özel işleme
          if (componentName === 'BlogList') {
            return `<div class="blog-list-component">
              ${convertJsxToHtml(rendered)}
            </div>`;
          }

          // Diğer bileşenler için genel işleme
          return `<div class="${componentName.toLowerCase()}-component">
            ${convertJsxToHtml(rendered)}
          </div>`;
        } catch (error) {
          console.error(`${componentName} bileşeni işlenirken hata:`, error);
          // Hata durumunda props.children'ı kullan
          return convertJsxToHtml(child.props.children) || '';
        }
      }

      // Normal HTML elementleri için
      if (typeof child.type === 'string') {
        return convertJsxToHtml(child);
      }

      // React Fragment veya diğer özel tipler için
      if (child.props?.children) {
        if (Array.isArray(child.props.children)) {
          return child.props.children.map(c => processComponent(c)).join('');
        }
        return processComponent(child.props.children);
      }

      return '';
    };
    
    // Children array ise her bir child'ı işle
    if (Array.isArray(children)) {
      content = children.map(child => processComponent(child)).join('');
    } else if (children) {
      content = processComponent(children);
    }
    
    return content;
  } catch (error) {
    console.error('Component content extraction error:', error);
    console.error('Rendered props:', rendered?.props);
    return '';
  }
};

// Gerçek sayfa içeriklerini Firebase'e kaydet
export const importRealPages = async () => {
  try {
    console.log('Gerçek sayfa içerikleri içe aktarılıyor...');
    
    // Önce mevcut sayfaları kontrol et
    const db = await getDb();
    const pagesRef = collection(db, PAGES_COLLECTION);
    const snapshot = await getDocs(pagesRef);
    const existingUrls = snapshot.docs.map(doc => doc.data().url);

    // Tüm sayfaları tanımla
    const pages = [
      // Ana Sayfalar
      { 
        component: () => import('../pages/Home.jsx'),
        url: '',
        title: 'Home'
      },
      { 
        component: () => import('../pages/WhoWeAre.jsx'),
        url: 'who-we-are',
        title: 'Who We Are'
      },
      { 
        component: () => import('../pages/AboutUs.jsx'),
        url: 'about-us',
        title: 'About Us'
      },
      { 
        component: () => import('../pages/Contact.jsx'),
        url: 'contact',
        title: 'Contact'
      },
      // Servis Sayfaları
      { 
        component: () => import('../pages/Services/index.jsx'),
        url: 'for-partners',
        title: 'Services'
      },
      { 
        component: () => import('../pages/Services/Headhunting.jsx'),
        url: 'for-partners/headhunting',
        title: 'Headhunting'
      },
      { 
        component: () => import('../pages/Services/Recruitment.jsx'),
        url: 'for-partners/recruitment',
        title: 'Recruitment'
      },
      { 
        component: () => import('../pages/Services/CareerConsulting.jsx'),
        url: 'for-partners/career-consulting',
        title: 'Career Consulting'
      },
      { 
        component: () => import('../pages/Services/PersonalityInventory.jsx'),
        url: 'for-partners/personality-inventory',
        title: 'Personality Inventory'
      },
      { 
        component: () => import('../pages/Services/HRPartnership.jsx'),
        url: 'for-partners/hr-partnership',
        title: 'HR Partnership'
      },
      { 
        component: () => import('../pages/Services/HRPartnership/HRProject.jsx'),
        url: 'for-partners/hr-partnership/hr-project',
        title: 'HR Project'
      },
      { 
        component: () => import('../pages/Services/HRPartnership/TalentMapping.jsx'),
        url: 'for-partners/hr-partnership/talent-mapping',
        title: 'Talent Mapping'
      },
      { 
        component: () => import('../pages/Services/HRPartnership/TalentConnect.jsx'),
        url: 'for-partners/hr-partnership/talent-connect',
        title: 'Talent Connect'
      },
      // Resources Sayfaları
      { 
        url: 'resources/blog',
      title: 'Blog',
        status: 'published'
      },
      { 
        url: 'resources/talent-talks',
        title: 'Talent Talks',
        status: 'published'
      },
      // İş Arama
      { 
        url: 'for-talents/opportunities',
        title: 'Job Opportunities',
        status: 'published'
      },
      // Yasal Sayfalar
      { 
        component: () => import('../pages/PrivacyPolicy.jsx'),
        url: 'privacy-policy',
        title: 'Privacy Policy'
      },
      { 
        component: () => import('../pages/PersonalDataDeclaration.jsx'),
        url: 'personal-data-declaration',
        title: 'Personal Data Declaration'
      }
    ];

    let addedCount = 0;
    let updatedCount = 0;
    let errorCount = 0;

    for (const page of pages) {
      try {
        // Slug oluştur
        const slug = generateSlug(page.title);

        // Sayfa verilerini hazırla
        const pageData = {
          title: page.title,
          url: page.url,
          content: page.content,
          metaTitle: `${page.title} | Talent Finder`,
          metaDescription: `${page.title} - Professional ${page.title.toLowerCase()} services by Talent Finder. ${page.url.includes('for-partners') ? 'Partner with us for expert HR solutions.' : 'Find the best talent and opportunities.'}`,
          slug: slug,
          status: page.status || 'published',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

        // Sayfayı Firebase'e kaydet
        if (existingUrls.includes(page.url)) {
          // Sayfa varsa güncelle
          const existingDoc = snapshot.docs.find(doc => doc.data().url === page.url);
          await updateDoc(existingDoc.ref, {
            ...pageData,
            updatedAt: serverTimestamp()
          });
          updatedCount++;
          console.log(`"${page.title}" sayfası güncellendi`);
        } else {
          // Yeni sayfa ekle
          await addDoc(pagesRef, pageData);
          addedCount++;
          console.log(`"${page.title}" sayfası eklendi`);
        }
      } catch (error) {
        console.error(`"${page.title}" sayfası işlenirken hata:`, error);
        console.error(error);
        errorCount++;
      }
    }

    return {
      success: true,
      message: `${addedCount} sayfa eklendi, ${updatedCount} sayfa güncellendi, ${errorCount} hata oluştu.`
    };
  } catch (error) {
    console.error('Sayfalar içe aktarılırken hata:', error);
    throw error;
  }
};

// Tüm sayfaları otomatik güncelle
export const updateAllPages = async () => {
  try {
    console.log('Tüm sayfalar güncelleniyor...');
    const db = await getDb();
    const pagesRef = collection(db, PAGES_COLLECTION);
    const snapshot = await getDocs(pagesRef);
    
    let updatedCount = 0;
    let errorCount = 0;
    
    for (const doc of snapshot.docs) {
      try {
        const pageData = doc.data();
        
        // Sayfa içeriğini hazırla
        const preparedData = preparePageData(pageData);
        
        // Sayfayı güncelle
        await updateDoc(doc.ref, {
          ...preparedData,
          updatedAt: serverTimestamp()
        });
        
        console.log(`"${pageData.title}" sayfası güncellendi`);
        updatedCount++;
      } catch (error) {
        console.error(`Sayfa güncellenirken hata: ${error.message}`);
        errorCount++;
      }
    }
    
    return {
      success: true,
      message: `${updatedCount} sayfa güncellendi, ${errorCount} hata oluştu.`
    };
  } catch (error) {
    console.error('Sayfalar güncellenirken hata:', error);
    throw error;
  }
};

export const editPage = async (pageId, pageData) => {
  try {
    const db = await getDb();
    const pageRef = doc(db, PAGES_COLLECTION, pageId);
    await updateDoc(pageRef, {
      ...pageData,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error editing page:', error);
    throw error;
  }
}; 